"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
  type RangeSelection,
  type TextFormatType,
} from "@payloadcms/richtext-lexical/lexical";
import {
  $createHeadingNode,
  $createQuoteNode,
} from "@payloadcms/richtext-lexical/lexical/rich-text";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@payloadcms/richtext-lexical/lexical/link";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from "@payloadcms/richtext-lexical/lexical/list";
import { $setBlocksType } from "@payloadcms/richtext-lexical/lexical/selection";
import { mergeRegister } from "@payloadcms/richtext-lexical/lexical/utils";
import { useLexicalComposerContext } from "@payloadcms/richtext-lexical/lexical/react/LexicalComposerContext";
import { INSERT_HORIZONTAL_RULE_COMMAND } from "@payloadcms/richtext-lexical/lexical/react/LexicalHorizontalRuleNode";
import { isSafeFormFunnelContentHref } from "@portal/app/_common/lib/formFunnelContent";
import { INSERT_TABLE_COMMAND } from "@lexical/table";

const formats: { label: string; format: TextFormatType; text: ReactNode }[] = [
  { label: "Bold", format: "bold", text: <strong>B</strong> },
  { label: "Italic", format: "italic", text: <em>I</em> },
  { label: "Underline", format: "underline", text: <u>U</u> },
  { label: "Strikethrough", format: "strikethrough", text: <s>S</s> },
  { label: "Inline code", format: "code", text: "<>" },
];

export function RichTextToolbar({ readOnly, id }: { readOnly?: boolean; id: string }) {
  const [editor] = useLexicalComposerContext();
  const [active, setActive] = useState<TextFormatType[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [newTab, setNewTab] = useState(false);
  const [linkError, setLinkError] = useState("");
  const savedSelection = useRef<RangeSelection | null>(null);

  useEffect(
    () =>
      mergeRegister(
        editor.registerUpdateListener(({ editorState }) => {
          editorState.read(() => {
            const selection = $getSelection();
            setActive(
              $isRangeSelection(selection)
                ? formats
                    .filter(({ format }) => selection.hasFormat(format))
                    .map(({ format }) => format)
                : []
            );
          });
        }),
        editor.registerCommand(
          CAN_UNDO_COMMAND,
          (enabled) => {
            setCanUndo(enabled);
            return false;
          },
          COMMAND_PRIORITY_LOW
        ),
        editor.registerCommand(
          CAN_REDO_COMMAND,
          (enabled) => {
            setCanRedo(enabled);
            return false;
          },
          COMMAND_PRIORITY_LOW
        )
      ),
    [editor]
  );

  const button = (
    label: string,
    action: () => void,
    text: ReactNode = label,
    pressed?: boolean,
    disabled = false
  ) => (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      disabled={readOnly || disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={action}
    >
      {text}
    </button>
  );
  const block = (kind: "paragraph" | "h2" | "h3" | "quote") =>
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      $setBlocksType(selection, () =>
        kind === "paragraph"
          ? $createParagraphNode()
          : kind === "quote"
            ? $createQuoteNode()
            : $createHeadingNode(kind)
      );
    });
  const openLink = () => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      savedSelection.current = selection.clone();
      const node = selection.anchor.getNode();
      const link = $isLinkNode(node) ? node : node.getParents().find($isLinkNode);
      setUrl(link?.getURL() ?? "");
      setNewTab(link?.getTarget() === "_blank");
      setLinkError("");
      setLinkOpen(true);
    });
  };
  const applyLink = (remove = false) => {
    const trimmed = url.trim();
    if (!remove && !isSafeFormFunnelContentHref(trimmed)) {
      setLinkError("Use a relative path, https:// URL, mailto: address, or tel: number.");
      return;
    }
    editor.update(() => {
      if (savedSelection.current) $setSelection(savedSelection.current.clone());
      editor.dispatchCommand(
        TOGGLE_LINK_COMMAND,
        remove
          ? null
          : {
              url: trimmed,
              target: newTab ? "_blank" : null,
              rel: newTab ? "noopener noreferrer" : null,
            }
      );
    });
    setLinkOpen(false);
    editor.focus();
  };
  return (
    <>
      <div role="toolbar" aria-label="Text formatting" className="lp-rich-text-toolbar">
        {formats.map(({ label, format, text }) => (
          <span key={format}>
            {button(
              label,
              () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, format),
              text,
              active.includes(format)
            )}
          </span>
        ))}
        {button("Paragraph", () => block("paragraph"), "¶")}
        {button("Heading 2", () => block("h2"), "H2")}
        {button("Heading 3", () => block("h3"), "H3")}
        {button("Quote", () => block("quote"), "❝")}
        {button(
          "Bulleted list",
          () => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined),
          "• List"
        )}
        {button(
          "Numbered list",
          () => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined),
          "1. List"
        )}
        {button("Edit link", openLink, "Link")}
        {button(
          "Insert table",
          () =>
            editor.dispatchCommand(INSERT_TABLE_COMMAND, {
              rows: "2",
              columns: "2",
              includeHeaders: true,
            }),
          "Table"
        )}
        {button(
          "Horizontal rule",
          () => editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined),
          "―"
        )}
        {button(
          "Undo text change",
          () => editor.dispatchCommand(UNDO_COMMAND, undefined),
          "↶",
          undefined,
          !canUndo
        )}
        {button(
          "Redo text change",
          () => editor.dispatchCommand(REDO_COMMAND, undefined),
          "↷",
          undefined,
          !canRedo
        )}
      </div>
      {linkOpen && (
        <div role="group" aria-label="Link settings" className="lp-rich-text-link-controls">
          <label htmlFor={`${id}-link-url`}>Link URL</label>
          <input
            id={`${id}-link-url`}
            type="text"
            value={url}
            disabled={readOnly}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://… or /page"
          />
          <label>
            <input
              type="checkbox"
              checked={newTab}
              disabled={readOnly}
              onChange={(event) => setNewTab(event.target.checked)}
            />{" "}
            Open in a new tab
          </label>
          {linkError && <p role="alert">{linkError}</p>}
          <div>
            {button("Apply link", () => applyLink())}
            {button("Remove link", () => applyLink(true))}
            {button(
              "Cancel link edit",
              () => {
                setLinkOpen(false);
                editor.focus();
              },
              "Cancel"
            )}
          </div>
        </div>
      )}
    </>
  );
}
